import {
  ADMIN_SETTINGS_CATEGORY_PATH,
  ADMIN_SETTINGS_PATH,
  APPLICATIONS_URL,
  APPROVAL_PROCESS_APPROVED_URL,
  APPROVAL_PROCESS_MY_APPLICATION_URL,
  APPROVAL_PROCESS_MY_RECEIVED_URL,
  APPROVAL_PROCESS_PENDING_APPROVAL_URL,
  APPROVAL_PROCESS_PROCESS_DESIGN_DETAIL_URL,
  APPROVAL_PROCESS_PROCESS_DESIGN_URL,
  APPROVAL_PROCESS_MY_FORM_URL,
  AUTH_LOGIN_URL,
  BASE_LOGIN_URL,
  BASE_SIGNUP_URL,
  BASE_URL,
  BUILDER_CUSTOM_PATH,
  BUILDER_PATCH_PATH,
  BUILDER_PATH,
  BUILDER_PATH_DEPRECATED,
  CUSTOM_WIDGETS_EDITOR_ID_PATH,
  CUSTOM_WIDGETS_EDITOR_ID_PATH_CUSTOM,
  DATA_MODELING_URL,
  DATA_MODEL_EDITOR_URL,
  DATA_MODEL_SETTING_URL,
  FOLDER_DETAIL_URL,
  HOME_URL,
  MODULES_URL,
  PROFILE,
  SETUP,
  SIGNUP_SUCCESS_URL,
  SIGN_UP_URL,
  TEMPLATES_PATH,
  TEMPLATES_URL,
  USERS_URL,
  USER_AUTH_URL,
  VIEWER_CUSTOM_PATH,
  VIEWER_PATCH_PATH,
  VIEWER_PATH,
  VIEWER_PATH_DEPRECATED,
  WORKFLOW_APPROVAL_URL,
  WORKSPACE_URL,
  AGENT_URL,
  WORKFLOW_URL,
} from "constants/routes";
import AppHeader from "ee/pages/common/AppHeader";
import ApplicationListLoader from "pages/Applications/loader";
import AppViewerLoader from "pages/AppViewer/loader";
import ErrorPage from "pages/common/ErrorPage";
import ErrorPageHeader from "pages/common/ErrorPageHeader";
import PageNotFound from "pages/common/ErrorPages/PageNotFound";
import PageLoadingBar from "pages/common/PageLoadingBar";
import EditorLoader from "pages/Editor/loader";
import UserAuth from "pages/UserAuth";
import Users from "pages/users";
import WorkspaceLoader from "pages/workspace/loader";
import { Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Router, Switch, useLocation } from "react-router-dom";
import history from "utils/history";
import LandingScreen from "../LandingScreen";

import * as Sentry from "@sentry/react";
import { getCurrentUser } from "actions/authActions";
import ProductAlertBanner from "components/editorComponents/ProductAlertBanner";
// import { fetchUsers } from "ee/actions/settingsAction";
import Walkthrough from "components/featureWalkthrough";
import { getAppsmithConfigs } from "ee/configs";
import type { ERROR_CODES } from "ee/constants/ApiConstants";
import { FEATURE_FLAG } from "ee/entities/FeatureFlag";
import {
  getTenantPermissions,
  isTenantLoading,
} from "ee/selectors/tenantSelectors";
import { getAdminSettingsPath } from "ee/utils/BusinessFeatures/adminSettingsHelpers";
import SettingsLoader from "pages/AdminSettings/loader";
import FolderListDetail from "pages/ApplicationModules/components/folderListDetail";
import DataModelEditor from "pages/DataModelEditor/DataModelEditorWrapper";
import DataModelSettingLoader from "pages/DataModelSetting/loader";
import CustomWidgetBuilderLoader from "pages/Editor/CustomWidgetBuilder/loader";
import Setup from "pages/setup";
import SignupSuccess from "pages/setup/SignupSuccess";
import TemplatesListLoader from "pages/Templates/loader";
import UserProfile from "pages/UserProfile";
import RouteChangeListener from "RouteChangeListener";
import { getSafeCrash, getSafeCrashCode } from "selectors/errorSelectors";
import { getIsConsolidatedPageLoading } from "selectors/ui";
import {
  getCurrentUser as getCurrentUserSelector,
  getCurrentUserLoading,
  getFeatureFlagsFetching,
} from "selectors/usersSelectors";
import useBrandingTheme from "utils/hooks/useBrandingTheme";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { initCurrentPage } from "../actions/initActions";
import { WebSocketProvider } from "./WebSocketProvider";

export const SentryRoute = Sentry.withSentryRouting(Route);

export const loadingIndicator = <PageLoadingBar />;
const { inCloudOS } = getAppsmithConfigs();

export function Routes() {
  const user = useSelector(getCurrentUserSelector);
  const tenantPermissions = useSelector(getTenantPermissions);
  const isFeatureEnabled = useFeatureFlag(FEATURE_FLAG.license_gac_enabled);
  const location = useLocation();
  return (
    <Switch>
      <SentryRoute component={LandingScreen} exact path={BASE_URL} />
      <Redirect exact from={BASE_LOGIN_URL} to={AUTH_LOGIN_URL} />
      <Redirect exact from={BASE_SIGNUP_URL} to={SIGN_UP_URL} />
      <SentryRoute component={WorkspaceLoader} path={WORKSPACE_URL} />
      <SentryRoute component={Users} exact path={USERS_URL} />
      <SentryRoute component={UserAuth} path={USER_AUTH_URL} />
      <SentryRoute
        component={DataModelSettingLoader}
        path={DATA_MODEL_SETTING_URL}
      />
      {/* <SentryRoute
        component={ApplicationListLoader}
        path={APPLICATIONS_URL}
      /> */}
      {/* <SentryRoute component={ApplicationModules} exact path={MODULES_URL} /> */}
      <SentryRoute
        component={FolderListDetail}
        exact
        path={FOLDER_DETAIL_URL}
      />
      {/* <SentryRoute component={ApplicationModules} exact path={MODULES_URL} /> */}
      {/* <SentryRoute
        component={FolderListDetail}
        exact
        path={FOLDER_DETAIL_URL}
      /> */}
      <SentryRoute component={SignupSuccess} exact path={SIGNUP_SUCCESS_URL} />
      <SentryRoute component={UserProfile} path={PROFILE} />
      <SentryRoute component={Setup} exact path={SETUP} />
      <SentryRoute component={TemplatesListLoader} path={TEMPLATES_PATH} />
      <Redirect
        exact
        from={ADMIN_SETTINGS_PATH}
        to={
          !user
            ? ADMIN_SETTINGS_PATH
            : getAdminSettingsPath(
                isFeatureEnabled,
                user?.isSuperUser || false,
                tenantPermissions,
              )
        }
      />
      <SentryRoute
        component={SettingsLoader}
        exact
        path={ADMIN_SETTINGS_CATEGORY_PATH}
      />
      <SentryRoute component={EditorLoader} path={BUILDER_PATH_DEPRECATED} />
      <SentryRoute component={AppViewerLoader} path={VIEWER_PATH_DEPRECATED} />
      <SentryRoute
        component={CustomWidgetBuilderLoader}
        exact
        path={CUSTOM_WIDGETS_EDITOR_ID_PATH}
      />
      <SentryRoute
        component={CustomWidgetBuilderLoader}
        exact
        path={CUSTOM_WIDGETS_EDITOR_ID_PATH_CUSTOM}
      />

      {/*
       * Note: When making changes to the order of these paths
       * Be sure to check if it is sync with the order of checks in getUpdatedRoute helper method
       * Context: https://github.com/appsmithorg/appsmith/pull/19833
       */}
      <SentryRoute component={EditorLoader} path={BUILDER_PATH} />
      <SentryRoute component={EditorLoader} path={BUILDER_CUSTOM_PATH} />
      <SentryRoute component={AppViewerLoader} path={VIEWER_PATH} />
      <SentryRoute component={AppViewerLoader} path={VIEWER_CUSTOM_PATH} />
      {/*
       * End Note: When making changes to the order of the paths above
       */}
      <Redirect from={BUILDER_PATCH_PATH} to={BUILDER_PATH} />
      <Redirect from={VIEWER_PATCH_PATH} to={VIEWER_PATH} />
      <SentryRoute exact path={HOME_URL} component={ApplicationListLoader} />
      <SentryRoute
        exact
        path={APPLICATIONS_URL}
        component={ApplicationListLoader}
      />
      <SentryRoute exact path={MODULES_URL} component={ApplicationListLoader} />
      <SentryRoute
        exact
        path={DATA_MODELING_URL}
        component={ApplicationListLoader}
      />
      <SentryRoute
        exact
        path={DATA_MODEL_EDITOR_URL}
        component={DataModelEditor}
      />
      <SentryRoute
        exact
        path={WORKFLOW_APPROVAL_URL}
        component={ApplicationListLoader}
      />
      <SentryRoute
        exact
        path={TEMPLATES_URL}
        component={ApplicationListLoader}
      />

      <SentryRoute
        exact
        path={APPROVAL_PROCESS_PROCESS_DESIGN_URL}
        component={ApplicationListLoader}
      />
      <SentryRoute
        exact
        path={APPROVAL_PROCESS_PROCESS_DESIGN_DETAIL_URL}
        component={ApplicationListLoader}
      />
      <SentryRoute
        exact
        path={APPROVAL_PROCESS_PENDING_APPROVAL_URL}
        component={ApplicationListLoader}
      />
      <SentryRoute
        exact
        path={APPROVAL_PROCESS_MY_APPLICATION_URL}
        component={ApplicationListLoader}
      />
      <SentryRoute
        exact
        path={APPROVAL_PROCESS_MY_RECEIVED_URL}
        component={ApplicationListLoader}
      />
      <SentryRoute
        exact
        path={APPROVAL_PROCESS_APPROVED_URL}
        component={ApplicationListLoader}
      />
      <SentryRoute
        exact
        path={APPROVAL_PROCESS_MY_FORM_URL}
        component={ApplicationListLoader}
      />
      <SentryRoute exact path={AGENT_URL} component={ApplicationListLoader} />
      <SentryRoute
        exact
        path={WORKFLOW_URL}
        component={ApplicationListLoader}
      />
      <SentryRoute component={PageNotFound} />
    </Switch>
  );
}

export default function AppRouter() {
  const safeCrash: boolean = useSelector(getSafeCrash);
  const safeCrashCode: ERROR_CODES | undefined = useSelector(getSafeCrashCode);
  const tenantIsLoading = useSelector(isTenantLoading);
  const currentUserIsLoading = useSelector(getCurrentUserLoading);
  const featuresIsLoading = useSelector(getFeatureFlagsFetching);
  const isConsolidatedPageLoading = useSelector(getIsConsolidatedPageLoading);
  const dispatch = useDispatch();
  const isConsolidatedFetchEnabled = useFeatureFlag(
    FEATURE_FLAG.rollout_consolidated_page_load_fetch_enabled,
  );
  const currentUser = useSelector(getCurrentUserSelector);

  useEffect(() => {
    // to fix km pro
    dispatch(getCurrentUser());
    dispatch(initCurrentPage());
    // ! 权限问题，不能在全局使用fetchUsers
    // 初始化时获取用户列表，使 global.users 在应用启动时就可用
    // dispatch(fetchUsers());
  }, []);

  useBrandingTheme();

  let isLoading: boolean;
  if (isConsolidatedFetchEnabled) {
    isLoading = isConsolidatedPageLoading;
  } else {
    isLoading = tenantIsLoading || currentUserIsLoading || featuresIsLoading;
  }
  // hide the top loader once the tenant is loaded
  useEffect(() => {
    if (!isLoading) {
      const loader = document.getElementById("loader") as HTMLDivElement;
      if (loader) {
        loader.style.width = "100vw";
        setTimeout(() => {
          loader.style.opacity = "0";
        });
      }
    }
  }, [isLoading]);

  if (isLoading) return null;
  return (
    <WebSocketProvider user={currentUser}>
      <Router history={history}>
        <Suspense fallback={loadingIndicator}>
          <RouteChangeListener />
          {safeCrash && safeCrashCode ? (
            <>
              <ErrorPageHeader />
              <ErrorPage code={safeCrashCode} />
            </>
          ) : inCloudOS ? (
            <Walkthrough>
              <AppHeader />
              <Switch>
                <SentryRoute
                  component={EditorLoader}
                  path={BUILDER_PATH_DEPRECATED}
                />
                <SentryRoute
                  component={AppViewerLoader}
                  path={VIEWER_PATH_DEPRECATED}
                />
                <SentryRoute component={EditorLoader} path={BUILDER_PATH} />
                <SentryRoute
                  component={EditorLoader}
                  path={BUILDER_CUSTOM_PATH}
                />
                <SentryRoute component={AppViewerLoader} path={VIEWER_PATH} />
                <SentryRoute
                  component={AppViewerLoader}
                  path={VIEWER_CUSTOM_PATH}
                />

                <Redirect from={BUILDER_PATCH_PATH} to={BUILDER_PATH} />
                <Redirect from={VIEWER_PATCH_PATH} to={VIEWER_PATH} />
                <SentryRoute component={PageNotFound} />
              </Switch>
            </Walkthrough>
          ) : (
            <>
              <Walkthrough>
                <AppHeader />
                <Routes />
              </Walkthrough>
              <ProductAlertBanner />
            </>
          )}
        </Suspense>
      </Router>
    </WebSocketProvider>
  );
}
